import React from 'react'
import { Typography } from 'antd'
import type { IntlShape } from 'react-intl'
import { EnergyMode, PTOSuggestionsContextInfo, ICompellingReason } from '@vacationtracker/shared/types/user-insight'
import { PTOSuggestionsResponse } from '../../../types'
import { translateCompellingReason } from '../utils/contextMessages'

const { Text } = Typography

interface ContextMessageProps {
  ptoSuggestions: PTOSuggestionsResponse
  formatMessage: IntlShape['formatMessage']
}

/**
 * Renders context message based on PTO suggestions context info
 */
export const ContextMessage: React.FC<ContextMessageProps> = ({ ptoSuggestions, formatMessage }) => {
  if (!ptoSuggestions.contextInfo) {
    return null
  }

  const { type, meta } = ptoSuggestions.contextInfo

  switch (type) {
    case PTOSuggestionsContextInfo.NORMAL: {
      const suggestionCount = ptoSuggestions.suggestions.length
      const adjustedMode = meta?.adjustedMode || EnergyMode.FUNCTIONAL
      const strategyMode = meta?.strategy?.mode || adjustedMode
      const wasAdjusted = meta?.wasAdjusted || false
      const minCount = meta?.strategy?.suggestionCount?.min || 1
      const maxCount = meta?.strategy?.suggestionCount?.max || 3
      const compellingReasons = meta?.compellingReasons || []

      return (
        <div>
          <div className="context-message-section">
            {formatMessage({ id: 'energyLevel.contextMessage.normal' }, { count: suggestionCount, plural: suggestionCount !== 1 ? 's' : '' })}
          </div>
          <div className={`context-message-section ${compellingReasons.length > 0 ? '' : 'no-margin'}`}>
            {formatMessage({ id: `energyLevel.recommendationReasoning.${strategyMode}` })}
          </div>
          <div className={`context-message-section ${compellingReasons.length > 0 ? '' : 'no-margin'}`}>
            {formatMessage(
              { id: 'energyLevel.contextMessage.normalDetails' },
              {
                adjustedMode,
                adjustedText: wasAdjusted ? formatMessage({ id: 'energyLevel.adjustedForExpiringPto' }) : '',
                minCount,
                maxCount,
              }
            )}
          </div>
          {compellingReasons.length > 0 && (
            <div className="context-message-top">
              {compellingReasons.map((reason: ICompellingReason, index: number) => (
                <div key={index} className={`context-message-item ${index < compellingReasons.length - 1 ? '' : 'no-margin'}`}>
                  <Text type="secondary" className="context-message-text">
                    • {translateCompellingReason(reason, formatMessage)}
                  </Text>
                </div>
              ))}
            </div>
          )}
        </div>
      )
    }

    case PTOSuggestionsContextInfo.NO_COMPELLING_REASON: {
      const adjustedMode = meta?.adjustedMode || EnergyMode.PEAK
      const wellbeingStatus = adjustedMode === EnergyMode.PEAK ? 'outstanding' : 'excellent'

      return (
        <div>
          <div className="context-message-section">{formatMessage({ id: 'energyLevel.contextMessage.noCompellingReason' }, { wellbeingStatus })}</div>
          <div>{formatMessage({ id: 'energyLevel.contextMessage.noCompellingReasonDetails' })}</div>
        </div>
      )
    }

    case PTOSuggestionsContextInfo.REQUIREMENTS_NOT_MET:
      return (
        <div>
          <div className="context-message-section">{formatMessage({ id: 'energyLevel.contextMessage.requirementsNotMet' })}</div>
          <div>{formatMessage({ id: 'energyLevel.contextMessage.requirementsNotMetDetails' })}</div>
        </div>
      )

    case PTOSuggestionsContextInfo.INSUFFICIENT_BALANCE:
      return (
        <div>
          <div className="context-message-section">{formatMessage({ id: 'energyLevel.contextMessage.insufficientBalance' })}</div>
          <div>
            {formatMessage(
              { id: 'energyLevel.contextMessage.insufficientBalanceDetails' },
              {
                currentBalance: meta?.currentBalance || 0,
                minBalance: meta?.minBalance || 1,
              }
            )}
          </div>
        </div>
      )

    case PTOSuggestionsContextInfo.RECENT_RETURN:
      return (
        <div>
          <div className="context-message-section">{formatMessage({ id: 'energyLevel.contextMessage.recentReturn' })}</div>
          <div>
            {formatMessage(
              { id: 'energyLevel.contextMessage.recentReturnDetails' },
              {
                daysSinceReturn: meta?.daysSinceReturn || 0,
                thresholdDays: meta && meta.recentLeaveThresholdDays && meta.daysSinceReturn ? meta.recentLeaveThresholdDays - meta.daysSinceReturn : 14,
              }
            )}
          </div>
        </div>
      )

    case PTOSuggestionsContextInfo.UPCOMING_LEAVE:
      return (
        <div>
          <div className="context-message-section">{formatMessage({ id: 'energyLevel.contextMessage.upcomingLeave' })}</div>
          <div>
            {formatMessage(
              { id: 'energyLevel.contextMessage.upcomingLeaveDetails' },
              {
                daysUntilLeave: meta?.daysUntilLeave || 0,
              }
            )}
          </div>
        </div>
      )

    default:
      return null
  }
}
